import React, { useCallback, useMemo } from 'react';
import {
  isTipoVenditaNazionale,
  isTipoVenditaQuotaFissa,
  isTipoVenditaTotalizzatore,
} from '../../utils/calcoloCarrello/utils';
import {
  selectErroreInserimento,
  selectIsScommettibile,
  selectIsSistema,
  selectTicket,
} from '../../ippicaTicketSelectors';
import { useAppDispatch, useTypedSelector } from 'lib/centralStore';
import {
  useScommettiSingolaMultiplaIppicaMutation,
  useScommettiSistemaIppicaMutation,
  useScommettiTotalizzatoreMutation,
  useScommettiTotalizzatoreNazionaleMutation,
} from 'features/api/ippicaApiSlice';

import { Button } from 'components/button';
import classNames from 'classnames';
import { setIsOpenLoginModalValue } from 'features/modal/modalSlice';
import styles from './IppicaTicketActionScommetti.module.scss';
import { useGetLabelsByKeys } from 'hooks/useLingUI';
import { useIsAuthenticated } from 'hooks/useIsAuthenticated';
import { useIsSaldoGiocabile } from 'features/carrello/hooks';

export type IppicaTicketActionScommettiProps = {
  btnClassName?: string;
};

export const IppicaTicketActionScommetti = ({ btnClassName }: IppicaTicketActionScommettiProps) => {
  const [scommettiButtonLabel] = useGetLabelsByKeys(['vendita-action-scommetti-button'])!;

  const isAuthenticated = useIsAuthenticated();

  const ticket = useTypedSelector(selectTicket);
  const isScommettibile = useTypedSelector(selectIsScommettibile);
  const isSistema = useTypedSelector(selectIsSistema);
  const erroreInserimento = useTypedSelector(selectErroreInserimento);
  const dispatch = useAppDispatch();

  const [scommettiSingolaMultiplaIppica, { isLoading: isLoadingSingolaMultipla }] =
    useScommettiSingolaMultiplaIppicaMutation();
  const [scommettiTotalizzatore, { isLoading: isLoadingTotalizzatore }] = useScommettiTotalizzatoreMutation();
  const [scommettiSistemaIppica, { isLoading: isLoadingSistema }] = useScommettiSistemaIppicaMutation();
  const [scommettiTotalizzatoreNazionale, { isLoading: isLoadingTotalizzatoreNazionale }] =
    useScommettiTotalizzatoreNazionaleMutation();
  const isSaldoGiocabile = useIsSaldoGiocabile();

  const canBet = useMemo(
    () =>
      [isScommettibile, ticket?.errori?.length === 0, erroreInserimento === undefined, isSaldoGiocabile].every(
        Boolean
      ) &&
      (!isLoadingSingolaMultipla || !isLoadingSistema || !isLoadingTotalizzatore || !isLoadingTotalizzatoreNazionale),
    [
      isScommettibile,
      ticket?.errori?.length,
      erroreInserimento,
      isSaldoGiocabile,
      isLoadingSingolaMultipla,
      isLoadingSistema,
      isLoadingTotalizzatore,
      isLoadingTotalizzatoreNazionale,
    ]
  );

  const onScommetti = useCallback(async () => {
    if (!isAuthenticated) {
      dispatch(setIsOpenLoginModalValue(true));
      return;
    }

    if (canBet && ticket) {
      let apiToCall: Function | undefined;
      if (isTipoVenditaQuotaFissa(ticket.tipoVendita)) {
        apiToCall = isSistema ? scommettiSistemaIppica : scommettiSingolaMultiplaIppica;
      } else if (isTipoVenditaTotalizzatore(ticket.tipoVendita)) {
        apiToCall = scommettiTotalizzatore;
      } else if (isTipoVenditaNazionale(ticket.tipoVendita)) {
        apiToCall = scommettiTotalizzatoreNazionale;
      }
      if (typeof apiToCall === 'function') {
        await apiToCall(ticket);
      }
    }
  }, [
    canBet,
    ticket,
    isSistema,
    scommettiSistemaIppica,
    scommettiSingolaMultiplaIppica,
    scommettiTotalizzatore,
    scommettiTotalizzatoreNazionale,
    dispatch,
    isAuthenticated,
  ]);
  const disableBtn = isAuthenticated && !canBet;

  return (
    <Button
      type="button"
      className={classNames(styles.button, btnClassName, { [styles.disable]: disableBtn })}
      disabled={disableBtn}
      size="medium"
      visualizationType={'primary'}
      onClick={() => onScommetti()}
    >
      {scommettiButtonLabel}
    </Button>
  );
};